import { MAX_INLINE_COMPONENT_DEPTH } from '../allergen-core/composition-limits';
import {
  ADDITIVE_FUNCTIONS,
  type AdditiveFunction,
} from '../dto/ingredient/additive-function.enum';
import {
  E_NUMBER_PATTERN,
  isValidDeclaredPercent,
} from '../dto/ingredient/ingredient-component-limits';
import {
  ADDITIVE_FUNCTION_LABELS,
  ADDITIVE_FUNCTION_PLURAL_LABELS,
  resolveIngredientListLocales,
} from './additive-function-labels';

/**
 * "Incolla l'elenco come sull'etichetta" (DEV-375): trasforma il testo di un
 * elenco ingredienti di un fornitore nell'albero delle voci di una scheda
 * ingrediente. Deterministico, nessuna IA. Le chiavi di `ParsedComponent` sono
 * quelle di `IngredientComponentDto` (nome, percentuale dichiarata, funzione e
 * numero E dell'additivo, sotto-voci): il chiamante aggiunge solo ordine,
 * provenienza e gli allergeni, che il parser non deduce MAI dal testo.
 *
 * Grammatica: separatori `,` e `;` fuori dalle parentesi (mai " e "); `()`,
 * `[]`, `{}` equivalenti fino a `maxNestingDepth` livelli sotto la voce;
 * `NN%`/`NN,N%` in coda alla voce diventa la percentuale (a meta' del testo o
 * con "min."/"max" resta nel nome, flag `PERCENT_KEPT_IN_NAME`); "puo'
 * contenere"/"tracce" si tagliano a ogni profondita'; "categoria: …" diventa la funzione
 * additivo; `E471` diventa il numero E.
 */

export interface ParsedComponent {
  name: string;
  declaredPercent?: number;
  additiveFunction?: AdditiveFunction;
  additiveENumber?: string;
  children?: ParsedComponent[];
}

export const LABEL_LIST_PARSE_FLAG_CODES = [
  'TRACES_IGNORED',
  'TOO_DEEP',
  'UNBALANCED',
  'UNKNOWN_PREFIX',
  'PERCENT_OUT_OF_RANGE',
  'PERCENT_KEPT_IN_NAME',
] as const;
export type LabelListParseFlagCode = (typeof LABEL_LIST_PARSE_FLAG_CODES)[number];

export interface LabelListParseFlag {
  code: LabelListParseFlagCode;
  /** Voce a cui si riferisce l'avviso; assente per `TRACES_IGNORED`. */
  name?: string;
}

export interface LabelListParseResult {
  components: ParsedComponent[];
  flags: LabelListParseFlag[];
}

export interface LabelListParseOptions {
  /**
   * Livelli di sotto-voci ammessi sotto le voci prodotte: 2 incollando al primo
   * livello, meno incollando dentro una sotto-voce. Oltre, la parentesi resta
   * testo nel nome (`TOO_DEEP`).
   */
  maxNestingDepth?: number;
}

const OPEN_BRACKETS = new Set(['(', '[', '{']);
const CLOSE_BRACKETS = new Set([')', ']', '}']);

const INGREDIENTS_HEADING = /^(?:ingredienti|ingredients|ingredient)\s*:\s*/i;
const PRECAUTIONARY_STATEMENT =
  /(?<![\p{L}\p{N}])(?:pu[oò]['’]?\s+contenere|tracce|may\s+contain|traces?\s+of|prodotto\s+in\s+uno\s+stabilimento)(?![\p{L}\p{N}])/giu;
const PERCENT_IN_TEXT = /\d+(?:[.,]\d+)?\s*%/;
/** La quota della voce sta in coda al suo testo ("cacao 53%", "cacao 53 %"). */
const PERCENT_AT_END = /(\d+(?:[.,]\d+)?)\s*%$/;
/**
 * "min. 50%", "max 5%": un limite, non la quota. Toglierlo dal nome
 * stamperebbe "cacao 50%" e cambierebbe il significato: resta nel nome.
 */
const QUALIFIED_PERCENT_AT_END = /(?<![\p{L}\p{N}])(?:min|max)(?:imo)?\.?\s*\d+(?:[.,]\d+)?\s*%$/iu;
const PERCENT_ONLY_GROUP = /^(\d+(?:[.,]\d+)?)\s*%$/;
const QUALIFIED_PERCENT_ONLY_GROUP = /^(?:min|max)(?:imo)?\.?\s*\d+(?:[.,]\d+)?\s*%$/i;
const E_NUMBER_IN_TEXT = /(?<![\p{L}\p{N}])E\s?(\d{3,4})([a-zA-Z]?)(?![\p{L}\p{N}])/u;
const E_NUMBER_ONLY_GROUP = /^E\s?(\d{3,4})([a-zA-Z]?)$/;
const SPLITTABLE_NAME_HINT = /[([{,;:]|E\s?\d{3}/;

/**
 * Forme che si leggono sulle etichette ma non coincidono con il nome
 * dell'Allegato VII Parte C (riconoscimento soltanto: la stampa usa sempre
 * `ADDITIVE_FUNCTION_LABELS`).
 */
const ADDITIVE_FUNCTION_ALIASES: ReadonlyArray<{
  label: string;
  additiveFunction: AdditiveFunction;
  isPlural: boolean;
}> = [
  { label: 'regolatore di acidità', additiveFunction: 'ACIDITY_REGULATOR', isPlural: false },
  { label: 'regolatori di acidità', additiveFunction: 'ACIDITY_REGULATOR', isPlural: true },
  { label: 'agente antiagglomerante', additiveFunction: 'ANTICAKING_AGENT', isPlural: false },
  { label: 'agenti antiagglomeranti', additiveFunction: 'ANTICAKING_AGENT', isPlural: true },
  { label: 'lievitante', additiveFunction: 'RAISING_AGENT', isPlural: false },
  { label: 'lievitanti', additiveFunction: 'RAISING_AGENT', isPlural: true },
  { label: 'color', additiveFunction: 'COLOUR', isPlural: false },
  { label: 'colors', additiveFunction: 'COLOUR', isPlural: true },
  { label: 'stabilizer', additiveFunction: 'STABILISER', isPlural: false },
  { label: 'stabilizers', additiveFunction: 'STABILISER', isPlural: true },
  { label: 'flavor enhancer', additiveFunction: 'FLAVOUR_ENHANCER', isPlural: false },
  { label: 'flavor enhancers', additiveFunction: 'FLAVOUR_ENHANCER', isPlural: true },
  { label: 'anticaking agent', additiveFunction: 'ANTICAKING_AGENT', isPlural: false },
  { label: 'anticaking agents', additiveFunction: 'ANTICAKING_AGENT', isPlural: true },
];

interface AdditiveCategoryMatch {
  additiveFunction: AdditiveFunction;
  isPlural: boolean;
}

interface RawGroup {
  text: string;
  items: RawItem[];
}

interface RawItem {
  /** Testo della voce fuori dalle parentesi riconosciute. */
  head: string;
  groups: RawGroup[];
  separatorBefore: ',' | ';' | null;
  /** Testo prima della parentesi rimasta letterale perche' troppo profonda. */
  tooDeepName: string | null;
}

function normalizeForLabelMatching(text: string): string {
  return text
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/['’]/g, '')
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase();
}

function buildCategoryIndex(locale: string): Map<string, AdditiveCategoryMatch> {
  const index = new Map<string, AdditiveCategoryMatch>();
  for (const tableLocale of resolveIngredientListLocales(locale)) {
    for (const additiveFunction of ADDITIVE_FUNCTIONS) {
      const singular = normalizeForLabelMatching(
        ADDITIVE_FUNCTION_LABELS[tableLocale][additiveFunction],
      );
      const plural = normalizeForLabelMatching(
        ADDITIVE_FUNCTION_PLURAL_LABELS[tableLocale][additiveFunction],
      );
      if (!index.has(singular)) {
        index.set(singular, { additiveFunction, isPlural: false });
      }
      if (!index.has(plural)) {
        index.set(plural, { additiveFunction, isPlural: true });
      }
    }
  }
  for (const alias of ADDITIVE_FUNCTION_ALIASES) {
    const key = normalizeForLabelMatching(alias.label);
    if (!index.has(key)) {
      index.set(key, { additiveFunction: alias.additiveFunction, isPlural: alias.isPlural });
    }
  }
  return index;
}

/**
 * Funzione additivo di un testo che e' solo un'etichetta di categoria
 * ("emulsionanti", "Gelling agent"), altrimenti null. Stessa tabella del
 * parser: la usa anche la normalizzazione dell'output IA per non lasciare una
 * categoria come voce.
 */
export function matchAdditiveCategoryLabel(text: string, locale: string): AdditiveFunction | null {
  return buildCategoryIndex(locale).get(normalizeForLabelMatching(text))?.additiveFunction ?? null;
}

function cleanName(text: string): string {
  return text
    .replace(/\s+/g, ' ')
    .replace(/\s+([,;:])/g, '$1')
    .replace(/^[\s.:,;-]+|[\s.:,;-]+$/g, '')
    .trim();
}

function isDecimalComma(text: string, index: number): boolean {
  return /\d/.test(text[index - 1] ?? '') && /\d/.test(text[index + 1] ?? '');
}

function bracketDepthAt(text: string, index: number): number {
  let depth = 0;
  for (let position = 0; position < index; position++) {
    if (OPEN_BRACKETS.has(text[position])) {
      depth++;
    } else if (CLOSE_BRACKETS.has(text[position])) {
      depth = Math.max(0, depth - 1);
    }
  }
  return depth;
}

/** Posizione della parentesi che chiude il gruppo in cui sta `index`, o la fine del testo. */
function enclosingCloseIndex(text: string, index: number): number {
  let depth = 0;
  for (let position = index; position < text.length; position++) {
    if (OPEN_BRACKETS.has(text[position])) {
      depth++;
    } else if (CLOSE_BRACKETS.has(text[position])) {
      if (depth === 0) {
        return position;
      }
      depth--;
    }
  }
  return text.length;
}

/**
 * Taglia "puo' contenere…"/"tracce…" a qualunque profondita': gli allergeni
 * precauzionali non sono voci. Fuori dalle parentesi si taglia il resto del
 * testo; dentro una parentesi solo fino alla sua chiusura, poi l'elenco
 * prosegue.
 */
function cutPrecautionaryStatement(text: string): { text: string; wasCut: boolean } {
  let remaining = text;
  let wasCut = false;
  for (;;) {
    PRECAUTIONARY_STATEMENT.lastIndex = 0;
    const match = PRECAUTIONARY_STATEMENT.exec(remaining);
    if (!match) {
      return { text: remaining, wasCut };
    }
    wasCut = true;
    if (bracketDepthAt(remaining, match.index) === 0) {
      return { text: remaining.slice(0, match.index), wasCut };
    }
    const kept = remaining.slice(0, match.index).replace(/[\s,;.]+$/, '');
    remaining = kept + remaining.slice(enclosingCloseIndex(remaining, match.index));
  }
}

/** Prima parentesi senza compagna: la piu' esterna aperta e mai chiusa, o una chiusa senza apertura. */
function findFirstUnbalancedBracket(text: string): number | null {
  const openIndexes: number[] = [];
  for (let position = 0; position < text.length; position++) {
    if (OPEN_BRACKETS.has(text[position])) {
      openIndexes.push(position);
    } else if (CLOSE_BRACKETS.has(text[position])) {
      if (openIndexes.length === 0) {
        return position;
      }
      openIndexes.pop();
    }
  }
  return openIndexes.length > 0 ? openIndexes[0] : null;
}

function lastTopLevelSeparatorBefore(text: string, index: number): number {
  let depth = 0;
  let lastSeparator = -1;
  for (let position = 0; position < index; position++) {
    const character = text[position];
    if (OPEN_BRACKETS.has(character)) {
      depth++;
    } else if (CLOSE_BRACKETS.has(character)) {
      depth = Math.max(0, depth - 1);
    } else if (
      depth === 0 &&
      (character === ';' || (character === ',' && !isDecimalComma(text, position)))
    ) {
      lastSeparator = position;
    }
  }
  return lastSeparator;
}

function matchingCloseIndex(text: string, openIndex: number): number {
  let depth = 0;
  for (let position = openIndex; position < text.length; position++) {
    if (OPEN_BRACKETS.has(text[position])) {
      depth++;
    } else if (CLOSE_BRACKETS.has(text[position])) {
      depth--;
      if (depth === 0) {
        return position;
      }
    }
  }
  return text.length - 1;
}

function emptyRawItem(separatorBefore: RawItem['separatorBefore']): RawItem {
  return { head: '', groups: [], separatorBefore, tooDeepName: null };
}

function hasContent(item: RawItem): boolean {
  return item.head.trim() !== '' || item.groups.length > 0;
}

function isListSeparator(text: string, position: number): boolean {
  const character = text[position];
  return character === ';' || (character === ',' && !isDecimalComma(text, position));
}

interface TokenizeBudget {
  depth: number;
  maxNestingDepth: number;
}

/**
 * Una parentesi aperta dentro `current`: diventa un gruppo di sotto-voci o,
 * oltre il budget di livelli, resta testo nel nome. Ritorna la posizione dopo
 * la parentesi chiusa.
 */
function consumeBracket(
  text: string,
  openIndex: number,
  current: RawItem,
  budget: TokenizeBudget,
): number {
  if (budget.depth >= budget.maxNestingDepth) {
    const closeIndex = matchingCloseIndex(text, openIndex);
    current.tooDeepName ??= cleanName(current.head);
    current.head += text.slice(openIndex, closeIndex + 1);
    return closeIndex + 1;
  }
  const nested = tokenizeLevel(text, openIndex + 1, {
    depth: budget.depth + 1,
    maxNestingDepth: budget.maxNestingDepth,
  });
  current.groups.push({ text: text.slice(openIndex + 1, nested.end), items: nested.items });
  current.head += ' ';
  return nested.end + 1;
}

/** Struttura di un livello di testo gia' bilanciato: voci, testo e gruppi fra parentesi. */
function tokenizeLevel(
  text: string,
  start: number,
  budget: TokenizeBudget,
): { items: RawItem[]; end: number } {
  const items: RawItem[] = [];
  let current = emptyRawItem(null);
  let position = start;
  while (position < text.length && !CLOSE_BRACKETS.has(text[position])) {
    if (isListSeparator(text, position)) {
      if (hasContent(current)) {
        items.push(current);
      }
      current = emptyRawItem(text[position] === ';' ? ';' : ',');
      position++;
    } else if (OPEN_BRACKETS.has(text[position])) {
      position = consumeBracket(text, position, current, budget);
    } else {
      current.head += text[position];
      position++;
    }
  }
  if (hasContent(current)) {
    items.push(current);
  }
  return { items, end: position };
}

function parsePercentValue(digits: string): number | null {
  const value = Number.parseFloat(digits.replace(',', '.'));
  return isValidDeclaredPercent(value) ? value : null;
}

function formatENumber(digits: string, suffix: string): string | null {
  const eNumber = `E${digits}${suffix.toLowerCase()}`;
  return E_NUMBER_PATTERN.test(eNumber) ? eNumber : null;
}

interface LevelContext {
  categoryIndex: Map<string, AdditiveCategoryMatch>;
  flags: LabelListParseFlag[];
}

interface InterpretedItem {
  components: ParsedComponent[];
  /** Categoria plurale ancora aperta per le voci successive. */
  openGroupFunction: AdditiveFunction | null;
}

function splitPrefix(head: string): { label: string; rest: string } | null {
  const colonIndex = head.indexOf(':');
  if (colonIndex < 0) {
    return null;
  }
  return { label: head.slice(0, colonIndex), rest: head.slice(colonIndex + 1) };
}

function readsAsENumberVoce(item: RawItem): boolean {
  return (
    E_NUMBER_IN_TEXT.test(item.head) ||
    item.groups.some((group) => E_NUMBER_ONLY_GROUP.test(group.text.trim()))
  );
}

function interpretLevel(items: readonly RawItem[], context: LevelContext): ParsedComponent[] {
  const components: ParsedComponent[] = [];
  let openGroupFunction: AdditiveFunction | null = null;
  for (const [index, item] of items.entries()) {
    if (item.separatorBefore === ';') {
      openGroupFunction = null;
    }
    const headWithoutHeading = index === 0 ? item.head.replace(INGREDIENTS_HEADING, '') : item.head;
    const interpreted = interpretItem(
      { ...item, head: headWithoutHeading },
      openGroupFunction,
      context,
    );
    components.push(...interpreted.components);
    openGroupFunction = interpreted.openGroupFunction;
  }
  return components;
}

interface ResolvedPrefix {
  head: string;
  additiveFunction: AdditiveFunction | null;
  openGroupFunction: AdditiveFunction | null;
  hasUnknownPrefix: boolean;
  /** Nessun ":" nella voce: puo' essere una categoria nuda seguita da parentesi. */
  hasColon: boolean;
}

/** "categoria: voce" (nota o sconosciuta) oppure la continuazione di un gruppo plurale aperto. */
function resolvePrefix(
  item: RawItem,
  inheritedGroupFunction: AdditiveFunction | null,
  context: LevelContext,
): ResolvedPrefix {
  const prefix = splitPrefix(item.head);
  if (prefix) {
    const category = context.categoryIndex.get(normalizeForLabelMatching(prefix.label));
    return category
      ? {
          head: prefix.rest,
          additiveFunction: category.additiveFunction,
          openGroupFunction: category.isPlural ? category.additiveFunction : null,
          hasUnknownPrefix: false,
          hasColon: true,
        }
      : {
          head: item.head,
          additiveFunction: null,
          openGroupFunction: null,
          hasUnknownPrefix: true,
          hasColon: true,
        };
  }
  const continuesGroup = inheritedGroupFunction !== null && readsAsENumberVoce(item);
  return {
    head: item.head,
    additiveFunction: continuesGroup ? inheritedGroupFunction : null,
    openGroupFunction: continuesGroup ? inheritedGroupFunction : null,
    hasUnknownPrefix: false,
    hasColon: false,
  };
}

/** "emulsionanti (E471, lecitina di soia)": la categoria passa a ogni voce fra parentesi. */
function expandBareCategory(
  item: RawItem,
  resolved: ResolvedPrefix,
  context: LevelContext,
): ParsedComponent[] | null {
  const bareCategory = resolved.hasColon
    ? undefined
    : context.categoryIndex.get(normalizeForLabelMatching(resolved.head));
  const listGroups = item.groups.filter((group) => !isAttributeGroup(group.text));
  if (!bareCategory || listGroups.length !== 1) {
    return null;
  }
  return interpretLevel(listGroups[0].items, context).map((component) => ({
    ...component,
    additiveFunction: component.additiveFunction ?? bareCategory.additiveFunction,
  }));
}

function interpretItem(
  item: RawItem,
  inheritedGroupFunction: AdditiveFunction | null,
  context: LevelContext,
): InterpretedItem {
  const resolved = resolvePrefix(item, inheritedGroupFunction, context);
  const expanded = expandBareCategory(item, resolved, context);
  if (expanded) {
    return { components: expanded, openGroupFunction: null };
  }
  const { openGroupFunction } = resolved;
  const component = buildComponent(resolved.head, item, resolved.additiveFunction, context);
  if (!component) {
    return { components: [], openGroupFunction };
  }
  if (resolved.hasUnknownPrefix) {
    context.flags.push({ code: 'UNKNOWN_PREFIX', name: component.name });
  }
  if (item.tooDeepName !== null) {
    context.flags.push({ code: 'TOO_DEEP', name: item.tooDeepName || component.name });
  }
  if (component.name === '') {
    return { components: component.children ?? [], openGroupFunction };
  }
  return { components: [component], openGroupFunction };
}

function isAttributeGroup(groupText: string): boolean {
  const trimmed = groupText.trim();
  return (
    PERCENT_ONLY_GROUP.test(trimmed) ||
    QUALIFIED_PERCENT_ONLY_GROUP.test(trimmed) ||
    E_NUMBER_ONLY_GROUP.test(trimmed)
  );
}

interface GroupAttributes {
  declaredPercent: number | null;
  additiveENumber: string | null;
  children: ParsedComponent[];
  /** "(min 30%)": resta nel nome cosi' com'e' scritto. */
  literalSuffixes: string[];
}

/** "(60%)" e "(E322)" sono attributi della voce; ogni altro gruppo e' un elenco di sotto-voci. */
function readGroups(item: RawItem, context: LevelContext): GroupAttributes {
  const attributes: GroupAttributes = {
    declaredPercent: null,
    additiveENumber: null,
    children: [],
    literalSuffixes: [],
  };
  for (const group of item.groups) {
    const trimmed = group.text.trim();
    const percentGroup = PERCENT_ONLY_GROUP.exec(trimmed);
    const groupPercent = percentGroup ? parsePercentValue(percentGroup[1]) : null;
    const eNumberGroup = E_NUMBER_ONLY_GROUP.exec(trimmed);
    if (QUALIFIED_PERCENT_ONLY_GROUP.test(trimmed)) {
      attributes.literalSuffixes.push(`(${trimmed})`);
    } else if (groupPercent !== null && attributes.declaredPercent === null) {
      attributes.declaredPercent = groupPercent;
    } else if (eNumberGroup && attributes.additiveENumber === null) {
      attributes.additiveENumber = formatENumber(eNumberGroup[1], eNumberGroup[2]);
    } else {
      attributes.children.push(...interpretLevel(group.items, context));
    }
  }
  return attributes;
}

function removeMatch(text: string, match: RegExpExecArray): string {
  return text.slice(0, match.index) + text.slice(match.index + match[0].length);
}

interface ExtractedPercent {
  head: string;
  value: number | null;
  outOfRange: boolean;
}

/**
 * Quota della voce: solo una percentuale in coda al testo ("cacao 53%").
 * Valida esce dal nome, fuori range resta e va segnalata. Una percentuale a
 * meta' ("latte intero 3,5% di grassi") o qualificata ("min. 50%") non e' la
 * quota della voce: resta nel nome (il chiamante la segnala).
 */
function extractPercent(rawHead: string): ExtractedPercent {
  const head = rawHead.trimEnd();
  const match = PERCENT_AT_END.exec(head);
  if (!match || QUALIFIED_PERCENT_AT_END.test(head)) {
    return { head: rawHead, value: null, outOfRange: false };
  }
  const value = parsePercentValue(match[1]);
  return value === null
    ? { head: rawHead, value: null, outOfRange: true }
    : { head: removeMatch(head, match), value, outOfRange: false };
}

/** Numero E nel testo della voce; se era tutto il nome, il nome resta il numero E. */
function extractENumber(head: string): { head: string; eNumber: string | null } {
  const match = E_NUMBER_IN_TEXT.exec(head);
  const eNumber = match ? formatENumber(match[1], match[2]) : null;
  if (!match || !eNumber) {
    return { head, eNumber: null };
  }
  const withoutENumber = cleanName(removeMatch(head, match));
  return { head: withoutENumber === '' ? eNumber : withoutENumber, eNumber };
}

function buildComponent(
  rawHead: string,
  item: RawItem,
  additiveFunction: AdditiveFunction | null,
  context: LevelContext,
): ParsedComponent | null {
  const groups = readGroups(item, context);
  const percent =
    groups.declaredPercent === null
      ? extractPercent(rawHead)
      : { head: rawHead, value: groups.declaredPercent, outOfRange: false };
  const eNumber =
    groups.additiveENumber === null
      ? extractENumber(percent.head)
      : { head: percent.head, eNumber: groups.additiveENumber };

  const name = cleanName([eNumber.head, ...groups.literalSuffixes].join(' '));
  if (name === '' && groups.children.length === 0) {
    return null;
  }
  if (percent.outOfRange) {
    context.flags.push({ code: 'PERCENT_OUT_OF_RANGE', name });
  } else if (PERCENT_IN_TEXT.test(name)) {
    context.flags.push({ code: 'PERCENT_KEPT_IN_NAME', name });
  }
  return {
    name,
    ...(percent.value !== null && { declaredPercent: percent.value }),
    ...(additiveFunction !== null && { additiveFunction }),
    ...(eNumber.eNumber !== null && { additiveENumber: eNumber.eNumber }),
    ...(groups.children.length > 0 && { children: groups.children }),
  };
}

function normalizeLabelText(text: string): string {
  return text.replace(/\s+/g, ' ').trim().replace(INGREDIENTS_HEADING, '');
}

function trimListEnd(text: string): string {
  return text.replace(/[\s.,;]+$/, '').trim();
}

/** Voci e avvisi di un elenco ingredienti come stampato sull'etichetta. */
export function parseLabelIngredientList(
  text: string,
  locale: string,
  options: LabelListParseOptions = {},
): LabelListParseResult {
  const budget: TokenizeBudget = {
    depth: 0,
    maxNestingDepth: options.maxNestingDepth ?? MAX_INLINE_COMPONENT_DEPTH,
  };
  const flags: LabelListParseFlag[] = [];
  const precautionaryCut = cutPrecautionaryStatement(normalizeLabelText(text));
  if (precautionaryCut.wasCut) {
    flags.push({ code: 'TRACES_IGNORED' });
  }
  const listText = trimListEnd(precautionaryCut.text);
  const context: LevelContext = { categoryIndex: buildCategoryIndex(locale), flags };

  const unbalancedIndex = findFirstUnbalancedBracket(listText);
  if (unbalancedIndex === null) {
    const { items } = tokenizeLevel(listText, 0, budget);
    return { components: interpretLevel(items, context), flags };
  }

  const literalStart = lastTopLevelSeparatorBefore(listText, unbalancedIndex) + 1;
  const { items } = tokenizeLevel(listText.slice(0, literalStart), 0, budget);
  const components = interpretLevel(items, context);
  const literalName = cleanName(listText.slice(literalStart));
  if (literalName !== '') {
    components.push({ name: literalName });
  }
  flags.push({
    code: 'UNBALANCED',
    name: cleanName(listText.slice(literalStart, unbalancedIndex)) || literalName,
  });
  return { components, flags };
}

export type SplittableNameDetection =
  | { kind: 'none' }
  | {
      kind: 'children';
      count: number;
      component: ParsedComponent;
      flags: LabelListParseFlag[];
    }
  | {
      kind: 'siblings';
      count: number;
      components: ParsedComponent[];
      flags: LabelListParseFlag[];
    }
  | { kind: 'additiveOnly'; component: ParsedComponent };

/**
 * Intercetto del campo Nome di una voce (on blur): stesso parser
 * dell'incolla. Una voce con parentesi propone le sotto-voci, piu' voci
 * propongono le sorelle, il solo prefisso additivo/numero E ripulisce il nome
 * in silenzio. Il chiamante passa `maxNestingDepth` residuo del livello.
 */
export function detectSplittableName(
  text: string,
  locale: string,
  options: LabelListParseOptions = {},
): SplittableNameDetection {
  if (!SPLITTABLE_NAME_HINT.test(text)) {
    return { kind: 'none' };
  }
  const { components, flags } = parseLabelIngredientList(text, locale, options);
  if (components.length >= 2) {
    return { kind: 'siblings', count: components.length, components, flags };
  }
  const [single] = components;
  if (!single) {
    return { kind: 'none' };
  }
  if (single.children && single.children.length > 0) {
    return { kind: 'children', count: single.children.length, component: single, flags };
  }
  if (single.additiveFunction || single.additiveENumber) {
    return { kind: 'additiveOnly', component: single };
  }
  return { kind: 'none' };
}
